import { CollectionMediaFileType } from '@pages/roboto-calls/utils/models/call-collection';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CrmLead } from '@utils/models/CrmLeadList';
import { IDebt } from '@utils/models/DebtList';
import { Edge, Node } from '@xyflow/react';

import { flowChatTypes } from '../constants';
import { IBlockAnswerModel } from '../models';

type NodeType = keyof typeof flowChatTypes;

interface IInitialState {
  open: boolean;
  nodeType?: NodeType;
  nodeId?: string;
  audio: {
    file: CollectionMediaFileType | null;
  };
  body: {
    name: string;
    description: string;
    dataType: 'DEBT' | 'LEAD';
    type: 'MESSAGE' | 'CALL';
    id?: string;
  };

  nodes: Node[];
  initialNodes: Node[];
  edges: Edge[];
  simulation: {
    open: boolean;
    selectedLead?: CrmLead;
    selectedContract?: IDebt;
    search: string;
  };
  messages: {
    id: string;
    question: string;
    simulateAnswer?: IBlockAnswerModel[];
    isTyping?: boolean;
  }[];
  isUpdate: boolean;
  agentTablePage: string;
  confirmOpen: boolean;
  activeBlockId: string | null;
  isSubmitted: boolean;
  llmBlocks: {
    name: string;
    key: string;
  }[];
  resultKey: 'FINISHED' | 'ERROR_OCCURRED' | 'IN_PROGRESS';
  openResultDrawer: {
    visible: boolean;
    id?: number;
  };
  deletedNodes?: string[];
  deletedEdges?: string[];
  deletedOptions?: string[];
}

const initialState: IInitialState = {
  open: false,
  nodeType: undefined,
  nodeId: undefined,
  audio: {
    file: null
  },
  body: {
    description: '',
    name: '',
    dataType: 'LEAD',
    type: 'MESSAGE',
    id: 'new'
  },
  nodes: [
    {
      id: '1_new',
      type: flowChatTypes.START,
      data: {
        name: 'Boshlanishi',
        description: '',
        json: {}
      },
      position: { x: 10, y: 25 }
    }
  ],
  edges: [],
  simulation: {
    open: false,
    selectedLead: undefined,
    selectedContract: undefined,
    search: ''
  },
  messages: [],
  isUpdate: false,
  agentTablePage: '1',
  confirmOpen: false,
  initialNodes: [],
  activeBlockId: null,
  isSubmitted: false,
  llmBlocks: [],
  resultKey: 'IN_PROGRESS',
  openResultDrawer: {
    visible: false,
    id: undefined
  },
  deletedNodes: [],
  deletedEdges: [],
  deletedOptions: []
};

const aiAgentSlice = createSlice({
  name: 'ai-agents-slice',
  initialState,
  reducers: {
    setOpenAIDrawer: (state, action: PayloadAction<{ open: boolean; nodeType?: NodeType; nodeId?: string }>) => {
      state.open = action.payload.open;
      state.nodeType = action.payload.nodeType;
      state.nodeId = action.payload.nodeId;
    },
    setAudio: (state, action: PayloadAction<Partial<IInitialState['audio']>>) => {
      state.audio = { ...state.audio, ...action.payload };
    },
    setBody: (state, action: PayloadAction<Partial<IInitialState['body']>>) => {
      state.body = { ...state.body, ...action.payload };
    },
    setNodes: (state, action: PayloadAction<(prev: Node[]) => Node[]>) => {
      state.nodes = action.payload(state.nodes);
    },
    setInitialNodes: (state, action: PayloadAction<(prev: Node[]) => Node[]>) => {
      state.initialNodes = action.payload(state.nodes);
    },
    setEdges: (state, action: PayloadAction<(prev: Edge[]) => Edge[]>) => {
      state.edges = action.payload(state.edges);
    },
    setUpdateNode: (state, action: PayloadAction<Node[]>) => {
      state.nodes = action.payload;
    },
    setSimulate: (state, action: PayloadAction<Partial<IInitialState['simulation']>>) => {
      state.simulation = { ...state.simulation, ...action.payload };
      state.open = false;
    },
    addQuestion: (state, action: PayloadAction<{ id: string; question: string }>) => {
      state.messages.push({
        id: action.payload.id,
        question: action.payload.question
      });
    },
    addTypingState: (state, action: PayloadAction<string>) => {
      const item = state.messages.find(msg => msg.id === action.payload);

      if (item) {
        item.isTyping = true;
      }
    },
    setAnswer: (state, action: PayloadAction<{ id: string; blockAnswers: IBlockAnswerModel }>) => {
      const item = state.messages.find(m => m.id === action.payload.id);

      if (item) {
        item.simulateAnswer = [...(item?.simulateAnswer || []), action.payload.blockAnswers];
        if (action?.payload?.blockAnswers?.lastBlock || action?.payload?.blockAnswers?.input) {
          item.isTyping = false;
        }
      }
    },
    clearMessages: state => {
      state.messages = [];
    },
    setIsUpdate: (state, action: PayloadAction<boolean>) => {
      state.isUpdate = action.payload;
    },
    setAgentTablePage: (state, action: PayloadAction<string>) => {
      state.agentTablePage = action.payload;
    },
    setConfirmOpen: (state, action: PayloadAction<boolean>) => {
      state.confirmOpen = action.payload;
    },
    setActiveBlockId: (state, action: PayloadAction<string | null>) => {
      state.activeBlockId = action.payload;
    },
    setIsSubmitted: (state, action: PayloadAction<boolean>) => {
      state.isSubmitted = action.payload;
    },
    setLlmBlocks: (state, action: PayloadAction<{ name: string; key: string }[]>) => {
      state.llmBlocks = action.payload;
    },
    setResultKey: (state, action: PayloadAction<'FINISHED' | 'ERROR_OCCURRED' | 'IN_PROGRESS'>) => {
      state.resultKey = action.payload;
    },
    setOpenResultDrawer: (state, action: PayloadAction<{ visible: boolean; id?: number }>) => {
      const { visible, id } = action?.payload;

      state.openResultDrawer.id = id;
      state.openResultDrawer.visible = visible;
    },
    setDeletedNodes: (state, action: PayloadAction<string | undefined>) => {
      state.deletedNodes = action?.payload ? [...(state.deletedNodes || []), action.payload] : undefined;
    },
    setDeletedEdges: (state, action: PayloadAction<string | undefined>) => {
      state.deletedEdges = action?.payload ? [...(state.deletedEdges || []), action.payload] : undefined;
    },
    setDeletedOptions: (state, action: PayloadAction<string | undefined>) => {
      state.deletedOptions = action?.payload ? [...(state.deletedOptions || []), action.payload] : undefined;
    },
    clearAiAgent: state => {
      state.nodes = [
        {
          id: '1_new',
          type: flowChatTypes.START,
          data: {
            name: 'Boshlanishi',
            description: '',
            json: {}
          },
          position: { x: 10, y: 25 }
        }
      ];
      state.edges = [];
      state.initialNodes = [];
      state.messages = [];
      state.body = { description: '', name: '', type: 'MESSAGE', id: undefined, dataType: 'LEAD' };
      state.simulation = { open: false, selectedLead: undefined, selectedContract: undefined, search: '' };
      state.open = false;
      state.nodeType = undefined;
      state.nodeId = undefined;
      state.deletedEdges = undefined;
      state.deletedNodes = undefined;
    }
  }
});

export const aiAgentReducer = aiAgentSlice.reducer;

export const {
  setAudio,
  setOpenAIDrawer,
  setBody,
  setNodes,
  setEdges,
  setUpdateNode,
  setSimulate,
  addQuestion,
  setAnswer,
  addTypingState,
  clearMessages,
  setIsUpdate,
  setAgentTablePage,
  setConfirmOpen,
  setInitialNodes,
  setActiveBlockId,
  setIsSubmitted,
  setLlmBlocks,
  setResultKey,
  setOpenResultDrawer,
  setDeletedNodes,
  setDeletedEdges,
  clearAiAgent,
  setDeletedOptions
} = aiAgentSlice.actions;
